import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { sessionService, type SessionsResponse } from '../services/session.service';

export const sessionKeys = {
  all: ['sessions'] as const,
  list: () => [...sessionKeys.all, 'list'] as const,
};

/** Resolves to `{ enabled, sessions }` -- see SessionsResponse for why `enabled` matters. */
export const useSessions = () =>
  useQuery<SessionsResponse, Error>({
    queryKey: sessionKeys.list(),
    queryFn: () => sessionService.list(),
    staleTime: 30_000,
  });

export const useRevokeSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sessionService.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sessionKeys.list() });
      toast.success('Session signed out');
    },
    onError: () => toast.error('Could not sign out that session'),
  });
};

/**
 * Revokes every other session. Resolves to `currentSessionKept` (see session.service.ts).
 *
 * When the current session survives, this refetches the list and toasts normally. When it
 * does NOT survive, this hook deliberately does nothing beyond that — no list refetch (the
 * caller's own session is dead, so refetching would just 401) and no success toast. The
 * caller (ActiveSessions.tsx) must pass its own `onSuccess` to `mutate()` to detect the
 * `false` case and redirect to login.
 */
export const useRevokeAllSessions = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<boolean> => sessionService.revokeAll(),
    onSuccess: (currentSessionKept) => {
      if (currentSessionKept) {
        qc.invalidateQueries({ queryKey: sessionKeys.list() });
        toast.success('Signed out of all other devices');
      }
    },
    onError: () => toast.error('Could not sign out the other devices'),
  });
};
